import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Button,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Space,
  Table,
  TimePicker,
  Upload,
  message,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  DownloadOutlined,
  EnvironmentOutlined,
  PaperClipOutlined,
  PlusOutlined,
  ReloadOutlined,
  StopOutlined,
  UploadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import moment from 'moment-jalaali';
import { useLocation } from 'react-router-dom';
import MainLayout from '../../../layouts/MainLayout';
import PersianDatePicker from '../../../components/PersianDatePicker';
import { TableSkeleton } from '../../../components/SkeletonLoaders';
import {
  attendanceMissionsApi,
  show422Error,
  ValidationError,
} from '../../../utils/api';
import { toPersianDigits } from '../../../utils/formatPriceToman';
import { toJalali } from '../../../utils/dateUtils';

const { TextArea } = Input;

const STATUS_META = {
  pending_manager: { color: 'processing', label: 'در انتظار مدیر' },
  pending_hr: { color: 'purple', label: 'در انتظار منابع انسانی' },
  approved: { color: 'success', label: 'تأیید شده' },
  rejected: { color: 'error', label: 'رد شده' },
  canceled: { color: 'default', label: 'لغو شده' },
};

const STATUS_BADGE_CLASS = {
  pending_manager: 'bg-amber-100 text-amber-800 border-amber-300',
  pending_hr: 'bg-purple-100 text-purple-800 border-purple-300',
  approved: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  rejected: 'bg-rose-100 text-rose-800 border-rose-300',
  canceled: 'bg-gray-100 text-gray-700 border-gray-300',
};

const TABS = [
  { key: 'my_missions', label: 'مأموریت‌های من', countKey: 'my_pending' },
  { key: 'manager_pending', label: 'کارتابل مدیر', countKey: 'manager_pending' },
  { key: 'hr_pending', label: 'کارتابل منابع انسانی', countKey: 'hr_pending' },
  { key: 'approved', label: 'تأییدشده‌ها', countKey: 'total_approved' },
];

const ACCEPTED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/jpg',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const MAX_FILE_BYTES = 10 * 1024 * 1024;

const jalaliToGregorian = (jalali) => {
  if (!jalali) return '';
  const normalized = String(jalali)
    .replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d))
    .replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d))
    .replace(/-/g, '/');
  const m = moment(normalized, 'jYYYY/jMM/jDD');
  return m.isValid() ? m.format('YYYY-MM-DD') : '';
};

const formatDisplayDate = (row, field = 'start') => {
  const jalaliKey = field === 'end' ? 'jalali_end_date' : 'jalali_start_date';
  const gregKey = field === 'end' ? 'end_date' : 'start_date';
  const rawJalali = row?.[jalaliKey];
  if (rawJalali) {
    const asText = String(rawJalali).trim().replace(/-/g, '/').slice(0, 10);
    if (/^\d{4}\/\d{2}\/\d{2}$/.test(asText)) return toPersianDigits(asText);
    const fromJalali = toJalali(asText);
    if (fromJalali) return toPersianDigits(fromJalali);
  }
  const gregorian = row?.[gregKey];
  if (gregorian) {
    const converted = toJalali(String(gregorian).slice(0, 10));
    if (converted) return toPersianDigits(converted);
  }
  return '—';
};

const formatMoney = (value) => {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return toPersianDigits(String(value));
  return toPersianDigits(n.toLocaleString('en-US'));
};

const formatTime = (value) => {
  if (value == null || value === '') return null;
  const s = String(value).trim();
  if (/^\d{1,2}:\d{2}/.test(s)) return toPersianDigits(s.slice(0, 5));
  const isoMatch = s.match(/T(\d{2}:\d{2})/);
  if (isoMatch) return toPersianDigits(isoMatch[1]);
  return toPersianDigits(s);
};

const MissionStatusBadge = ({ status, label }) => {
  const cls = STATUS_BADGE_CLASS[status] || 'bg-gray-100 text-gray-700 border-gray-300';
  const text = label || STATUS_META[status]?.label || status || '—';
  return (
    <span
      className={`inline-flex px-2.5 py-1 text-xs rounded-full border font-medium ${cls}`}
      style={{ fontFamily: 'CustomFont' }}
    >
      {text}
    </span>
  );
};

const MissionRequests = () => {
  const location = useLocation();
  const navState = location.state || {};
  const [tab, setTab] = useState(navState.tab || 'my_missions');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({
    my_pending: 0,
    my_approved: 0,
    manager_pending: 0,
    hr_pending: 0,
    total_approved: 0,
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [total, setTotal] = useState(0);

  const [createOpen, setCreateOpen] = useState(Boolean(navState.openCreate));
  const [createLoading, setCreateLoading] = useState(false);
  const [fileList, setFileList] = useState([]);

  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [managerOpen, setManagerOpen] = useState(false);
  const [managerTarget, setManagerTarget] = useState(null);
  const [hrOpen, setHrOpen] = useState(false);
  const [hrTarget, setHrTarget] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);

  const [form] = Form.useForm();
  const [rejectForm] = Form.useForm();
  const [managerForm] = Form.useForm();
  const [hrForm] = Form.useForm();
  const missionType = Form.useWatch('type', form);

  const loadCounts = useCallback(async () => {
    try {
      const res = await attendanceMissionsApi.getCounts();
      const data = res?.data || res || {};
      setCounts({
        my_pending: data.my_pending || 0,
        my_approved: data.my_approved || 0,
        manager_pending: data.manager_pending || 0,
        hr_pending: data.hr_pending || 0,
        total_approved: data.total_approved || 0,
      });
    } catch (e) {
      console.warn('mission counts:', e?.message || e);
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await attendanceMissionsApi.getAll({
        view_type: tab,
        page,
        limit: pageSize,
        per_page: pageSize,
      });
      const data = res?.data || res || {};
      const list = Array.isArray(data.missions)
        ? data.missions
        : Array.isArray(data.data)
          ? data.data
          : Array.isArray(data)
            ? data
            : [];
      setRows(list);
      if (data.counts) {
        setCounts((prev) => ({ ...prev, ...data.counts }));
      }
      setTotal(data.pagination?.total ?? data.meta?.total ?? list.length);
    } catch (error) {
      console.error(error);
      message.error(error?.message || 'خطا در دریافت درخواست‌های مأموریت');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [tab, page, pageSize]);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    if (!createOpen) {
      setFileList([]);
      return;
    }
    form.setFieldsValue({
      type: 'daily',
      trip_type: 'inside_city',
      destination: '',
      start_date_jalali: '',
      end_date_jalali: '',
      start_time: null,
      end_time: null,
      purpose: '',
      estimated_cost: undefined,
    });
  }, [createOpen, form]);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      const startDate = jalaliToGregorian(values.start_date_jalali);
      const endDate = jalaliToGregorian(values.end_date_jalali);
      if (!startDate || !endDate) {
        message.error('تاریخ شروع و پایان معتبر وارد کنید');
        return;
      }
      if (endDate < startDate) {
        message.error('تاریخ پایان باید بزرگ‌تر یا مساوی تاریخ شروع باشد');
        return;
      }
      if (values.type === 'hourly') {
        if (!values.start_time || !values.end_time) {
          message.error('برای مأموریت ساعتی، ساعت شروع و پایان الزامی است');
          return;
        }
      }

      setCreateLoading(true);
      const formData = new FormData();
      formData.append('type', values.type);
      formData.append('trip_type', values.trip_type);
      formData.append('destination', values.destination.trim());
      formData.append('start_date', startDate);
      formData.append('end_date', endDate);
      formData.append('purpose', values.purpose.trim());
      if (values.type === 'hourly') {
        formData.append('start_time', values.start_time.format('HH:mm'));
        formData.append('end_time', values.end_time.format('HH:mm'));
      }
      if (values.estimated_cost != null && values.estimated_cost !== '') {
        formData.append('estimated_cost', String(values.estimated_cost));
      }
      const file = fileList[0]?.originFileObj || fileList[0];
      if (file instanceof File || file instanceof Blob) {
        formData.append('attachment', file);
      }

      await attendanceMissionsApi.create(formData);
      message.success('درخواست مأموریت ثبت و برای مدیر ارسال شد');
      setCreateOpen(false);
      form.resetFields();
      setFileList([]);
      loadCounts();
      if (tab !== 'my_missions') setTab('my_missions');
      else loadList();
    } catch (error) {
      if (error?.errorFields) return;
      if (error instanceof ValidationError || error?.error?.details) {
        show422Error(error, form);
      } else {
        message.error(error?.message || 'خطا در ثبت درخواست مأموریت');
      }
    } finally {
      setCreateLoading(false);
    }
  };

  const runAction = async (id, fn, successMsg) => {
    setActionLoadingId(id);
    try {
      await fn();
      message.success(successMsg);
      loadCounts();
      loadList();
    } catch (error) {
      message.error(error?.message || 'خطا در انجام عملیات');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleManagerApprove = async () => {
    try {
      const values = await managerForm.validateFields();
      if (!managerTarget?.id) return;
      await runAction(
        managerTarget.id,
        () =>
          attendanceMissionsApi.managerApprove(managerTarget.id, {
            comment: values.comment || undefined,
          }),
        'تأیید مدیر ثبت شد',
      );
      setManagerOpen(false);
      setManagerTarget(null);
      managerForm.resetFields();
    } catch (error) {
      if (error?.errorFields) return;
    }
  };

  const handleHrApprove = async () => {
    try {
      const values = await hrForm.validateFields();
      if (!hrTarget?.id) return;
      await runAction(
        hrTarget.id,
        () =>
          attendanceMissionsApi.hrApprove(hrTarget.id, {
            comment: values.comment || undefined,
          }),
        'مأموریت تأیید نهایی شد',
      );
      setHrOpen(false);
      setHrTarget(null);
      hrForm.resetFields();
    } catch (error) {
      if (error?.errorFields) return;
    }
  };

  const handleReject = async () => {
    try {
      const values = await rejectForm.validateFields();
      if (!rejectTarget?.id) return;
      await runAction(
        rejectTarget.id,
        () =>
          attendanceMissionsApi.reject(rejectTarget.id, {
            reason: values.reason,
          }),
        'درخواست مأموریت رد شد',
      );
      setRejectOpen(false);
      setRejectTarget(null);
      rejectForm.resetFields();
    } catch (error) {
      if (error?.errorFields) return;
    }
  };

  const handleDownload = async (row) => {
    if (!row?.id) return;
    try {
      await attendanceMissionsApi.downloadAttachment(
        row.id,
        row.attachment_name || 'mission-attachment',
      );
    } catch (error) {
      message.error(error?.message || 'خطا در دانلود فایل پیوست');
    }
  };

  const openDetail = async (row) => {
    setDetailRow(row);
    setDetailOpen(true);
    try {
      const res = await attendanceMissionsApi.getById(row.id);
      const data = res?.data || res || {};
      const mission = data.mission || data;
      if (mission?.id) setDetailRow(mission);
    } catch (e) {
      console.warn('mission detail:', e?.message || e);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: <span style={{ fontFamily: 'CustomFont' }}>کارمند</span>,
        key: 'user',
        width: 180,
        render: (_, row) => (
          <div className="flex items-center gap-2" style={{ fontFamily: 'CustomFont' }}>
            <Avatar size={32} src={row.user?.image} icon={<UserOutlined />} />
            <div>
              <div className="font-medium">{row.user?.name || '—'}</div>
              <div className="text-xs text-gray-500">{row.user?.email || '—'}</div>
            </div>
          </div>
        ),
      },
      {
        title: <span style={{ fontFamily: 'CustomFont' }}>نوع</span>,
        key: 'type',
        width: 120,
        render: (_, row) => (
          <div style={{ fontFamily: 'CustomFont' }} className="text-sm space-y-0.5">
            <div>{row.type_label || (row.type === 'hourly' ? 'ساعتی' : 'روزانه')}</div>
            <div className="text-xs text-slate-500">
              {row.trip_type_label ||
                (row.trip_type === 'outside_city' ? 'برون‌شهری' : 'درون‌شهری')}
            </div>
          </div>
        ),
      },
      {
        title: <span style={{ fontFamily: 'CustomFont' }}>مقصد</span>,
        dataIndex: 'destination',
        key: 'destination',
        ellipsis: true,
        render: (text) => (
          <span style={{ fontFamily: 'CustomFont' }} title={text || ''}>
            {text || '—'}
          </span>
        ),
      },
      {
        title: <span style={{ fontFamily: 'CustomFont' }}>شروع / پایان</span>,
        key: 'dates',
        width: 160,
        render: (_, row) => (
          <div style={{ fontFamily: 'CustomFont' }} className="text-sm tabular-nums" dir="ltr">
            <div>{formatDisplayDate(row, 'start')}</div>
            <div className="text-slate-500">{formatDisplayDate(row, 'end')}</div>
            {row.type === 'hourly' && (row.start_time || row.end_time) ? (
              <div className="text-xs text-indigo-700 mt-0.5">
                {formatTime(row.start_time)} – {formatTime(row.end_time)}
              </div>
            ) : null}
            {row.days_count != null ? (
              <div className="text-[11px] text-slate-400">
                {toPersianDigits(row.days_count)} روز
              </div>
            ) : null}
          </div>
        ),
      },
      {
        title: <span style={{ fontFamily: 'CustomFont' }}>هدف / هزینه</span>,
        key: 'purpose',
        render: (_, row) => (
          <div style={{ fontFamily: 'CustomFont' }} className="text-sm space-y-0.5">
            <div className="line-clamp-2" title={row.purpose || ''}>
              {row.purpose || '—'}
            </div>
            <div className="text-xs text-slate-500 tabular-nums">
              هزینه: {formatMoney(row.estimated_cost)}
            </div>
            {row.rejection_reason ? (
              <div className="text-xs text-red-600">رد: {row.rejection_reason}</div>
            ) : null}
          </div>
        ),
      },
      {
        title: <span style={{ fontFamily: 'CustomFont' }}>فایل</span>,
        key: 'attachment',
        width: 90,
        render: (_, row) =>
          row.attachment_path || row.attachment_name || row.attachment_url ? (
            <Button
              size="small"
              type="link"
              icon={<DownloadOutlined />}
              style={{ fontFamily: 'CustomFont', paddingInline: 0 }}
              onClick={() => handleDownload(row)}
            >
              دانلود
            </Button>
          ) : (
            <span className="text-xs text-slate-400" style={{ fontFamily: 'CustomFont' }}>
              —
            </span>
          ),
      },
      {
        title: <span style={{ fontFamily: 'CustomFont' }}>وضعیت</span>,
        key: 'status',
        width: 140,
        render: (_, row) => (
          <MissionStatusBadge status={row.status} label={row.status_label} />
        ),
      },
      {
        title: <span style={{ fontFamily: 'CustomFont' }}>عملیات</span>,
        key: 'actions',
        width: 240,
        render: (_, row) => {
          const busy = actionLoadingId === row.id;
          const canCancel =
            tab === 'my_missions' &&
            (row.status === 'pending_manager' || row.status === 'pending_hr');
          return (
            <Space wrap size={[4, 4]}>
              <Button
                size="small"
                style={{ fontFamily: 'CustomFont' }}
                onClick={() => openDetail(row)}
              >
                جزئیات
              </Button>
              {tab === 'manager_pending' && row.status === 'pending_manager' ? (
                <>
                  <Button
                    size="small"
                    type="primary"
                    icon={<CheckOutlined />}
                    loading={busy}
                    style={{ fontFamily: 'CustomFont' }}
                    onClick={() => {
                      setManagerTarget(row);
                      managerForm.setFieldsValue({ comment: '' });
                      setManagerOpen(true);
                    }}
                  >
                    تأیید مدیر
                  </Button>
                  <Button
                    size="small"
                    danger
                    icon={<CloseOutlined />}
                    loading={busy}
                    style={{ fontFamily: 'CustomFont' }}
                    onClick={() => {
                      setRejectTarget(row);
                      rejectForm.setFieldsValue({ reason: '' });
                      setRejectOpen(true);
                    }}
                  >
                    رد
                  </Button>
                </>
              ) : null}
              {tab === 'hr_pending' && row.status === 'pending_hr' ? (
                <>
                  <Button
                    size="small"
                    type="primary"
                    icon={<CheckOutlined />}
                    loading={busy}
                    className="bg-indigo-600 hover:bg-indigo-700 border-indigo-600"
                    style={{ fontFamily: 'CustomFont' }}
                    onClick={() => {
                      setHrTarget(row);
                      hrForm.setFieldsValue({ comment: '' });
                      setHrOpen(true);
                    }}
                  >
                    تأیید نهایی
                  </Button>
                  <Button
                    size="small"
                    danger
                    icon={<CloseOutlined />}
                    loading={busy}
                    style={{ fontFamily: 'CustomFont' }}
                    onClick={() => {
                      setRejectTarget(row);
                      rejectForm.setFieldsValue({ reason: '' });
                      setRejectOpen(true);
                    }}
                  >
                    رد
                  </Button>
                </>
              ) : null}
              {canCancel ? (
                <Button
                  size="small"
                  icon={<StopOutlined />}
                  loading={busy}
                  style={{ fontFamily: 'CustomFont' }}
                  onClick={() =>
                    runAction(
                      row.id,
                      () => attendanceMissionsApi.cancel(row.id),
                      'درخواست مأموریت لغو شد',
                    )
                  }
                >
                  لغو
                </Button>
              ) : null}
            </Space>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tab, actionLoadingId],
  );

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }}>
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold m-0 flex items-center gap-2">
              <EnvironmentOutlined className="text-indigo-600" />
              درخواست مأموریت
            </h2>
          </div>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                loadCounts();
                loadList();
              }}
              loading={loading}
            >
              بروزرسانی
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
              درخواست مأموریت
            </Button>
          </Space>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 mb-4">
          {TABS.map((t) => {
            const count = counts[t.countKey] || 0;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => {
                  setTab(t.key);
                  setPage(1);
                }}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs border transition-all leading-none"
                style={{
                  fontFamily: 'CustomFont',
                  background: active ? '#4338ca' : '#fff',
                  color: active ? '#fff' : '#475569',
                  borderColor: active ? '#4338ca' : '#e2e8f0',
                  fontWeight: active ? 600 : 500,
                }}
              >
                {t.label}
                {count > 0 ? (
                  <span
                    className="inline-flex items-center justify-center min-w-[1.1rem] h-4 px-1 rounded-full text-[10px] font-semibold tabular-nums"
                    style={{
                      backgroundColor: active ? 'rgba(255,255,255,0.22)' : '#eef2ff',
                      color: active ? '#fff' : '#4338ca',
                    }}
                  >
                    {toPersianDigits(count)}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>

        {loading && !rows.length ? (
          <TableSkeleton rows={5} columns={7} />
        ) : (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={rows}
            loading={loading}
            scroll={{ x: 1100 }}
            locale={{
              emptyText: (
                <Empty
                  description={
                    <span style={{ fontFamily: 'CustomFont' }}>مأموریتی یافت نشد</span>
                  }
                />
              ),
            }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              onChange: (p, size) => {
                setPage(p);
                setPageSize(size);
              },
            }}
          />
        )}

        {/* ثبت درخواست */}
        <Modal
          title={
            <span style={{ fontFamily: 'CustomFont' }} className="inline-flex items-center gap-2">
              <EnvironmentOutlined />
              ثبت درخواست مأموریت
            </span>
          }
          open={createOpen}
          onCancel={() => setCreateOpen(false)}
          onOk={handleCreate}
          confirmLoading={createLoading}
          okText="ارسال برای مدیر"
          cancelText="انصراف"
          destroyOnClose
          width={560}
        >
          <Form form={form} layout="vertical" className="mt-2" style={{ fontFamily: 'CustomFont' }}>
            <Form.Item
              name="type"
              label="نوع مأموریت"
              rules={[{ required: true, message: 'نوع مأموریت الزامی است' }]}
            >
              <Radio.Group>
                <Radio value="daily">روزانه</Radio>
                <Radio value="hourly">ساعتی</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              name="trip_type"
              label="محدوده جغرافیایی"
              rules={[{ required: true, message: 'محدوده الزامی است' }]}
            >
              <Radio.Group>
                <Radio value="inside_city">درون‌شهری</Radio>
                <Radio value="outside_city">برون‌شهری</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              name="destination"
              label="مقصد"
              rules={[
                { required: true, message: 'مقصد الزامی است' },
                { min: 2, message: 'مقصد را کامل‌تر وارد کنید' },
              ]}
            >
              <Input placeholder="مقصد مأموریت را وارد کنید" />
            </Form.Item>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
              <Form.Item
                name="start_date_jalali"
                label="تاریخ شروع"
                rules={[{ required: true, message: 'تاریخ شروع الزامی است' }]}
              >
                <PersianDatePicker
                  placeholder="تاریخ شروع"
                  style={{ width: '100%' }}
                  onChange={(val) => form.setFieldsValue({ start_date_jalali: val })}
                />
              </Form.Item>
              <Form.Item
                name="end_date_jalali"
                label="تاریخ پایان"
                rules={[{ required: true, message: 'تاریخ پایان الزامی است' }]}
              >
                <PersianDatePicker
                  placeholder="تاریخ پایان"
                  style={{ width: '100%' }}
                  onChange={(val) => form.setFieldsValue({ end_date_jalali: val })}
                />
              </Form.Item>
            </div>

            {missionType === 'hourly' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
                <Form.Item
                  name="start_time"
                  label="ساعت شروع"
                  rules={[{ required: true, message: 'ساعت شروع الزامی است' }]}
                >
                  <TimePicker format="HH:mm" style={{ width: '100%' }} needConfirm={false} />
                </Form.Item>
                <Form.Item
                  name="end_time"
                  label="ساعت پایان"
                  rules={[{ required: true, message: 'ساعت پایان الزامی است' }]}
                >
                  <TimePicker format="HH:mm" style={{ width: '100%' }} needConfirm={false} />
                </Form.Item>
              </div>
            ) : null}

            <Form.Item
              name="purpose"
              label="هدف مأموریت"
              rules={[
                { required: true, message: 'هدف مأموریت الزامی است' },
                { min: 5, message: 'حداقل ۵ کاراکتر' },
              ]}
            >
              <TextArea rows={3} placeholder="شرح هدف مأموریت" />
            </Form.Item>

            <Form.Item name="estimated_cost" label="برآورد هزینه">
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(v) => v.replace(/,/g, '')}
                placeholder="مبلغ"
              />
            </Form.Item>

            <Form.Item label="فایل پیوست">
              <Upload
                beforeUpload={(file) => {
                  if (file.size > MAX_FILE_BYTES) {
                    message.error('حداکثر حجم فایل ۱۰ مگابایت است');
                    return Upload.LIST_IGNORE;
                  }
                  if (file.type && !ACCEPTED_TYPES.includes(file.type)) {
                    message.error('فرمت مجاز: پی‌دی‌اف، تصویر یا ورد');
                    return Upload.LIST_IGNORE;
                  }
                  return false;
                }}
                fileList={fileList}
                onChange={({ fileList: fl }) => setFileList(fl.slice(-1))}
                maxCount={1}
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              >
                <Button icon={<UploadOutlined />} style={{ fontFamily: 'CustomFont' }}>
                  انتخاب فایل
                </Button>
              </Upload>
            </Form.Item>
          </Form>
        </Modal>

        {/* تأیید مدیر */}
        <Modal
          title={<span style={{ fontFamily: 'CustomFont' }}>تأیید مأموریت توسط مدیر</span>}
          open={managerOpen}
          onCancel={() => {
            setManagerOpen(false);
            setManagerTarget(null);
          }}
          onOk={handleManagerApprove}
          confirmLoading={actionLoadingId === managerTarget?.id}
          okText="تأیید"
          cancelText="انصراف"
          destroyOnClose
        >
          <Form form={managerForm} layout="vertical" style={{ fontFamily: 'CustomFont' }}>
            <Form.Item name="comment" label="یادداشت">
              <TextArea rows={3} placeholder="در صورت نیاز یادداشت بنویسید" />
            </Form.Item>
          </Form>
        </Modal>

        {/* تأیید منابع انسانی */}
        <Modal
          title={<span style={{ fontFamily: 'CustomFont' }}>تأیید نهایی مأموریت</span>}
          open={hrOpen}
          onCancel={() => {
            setHrOpen(false);
            setHrTarget(null);
          }}
          onOk={handleHrApprove}
          confirmLoading={actionLoadingId === hrTarget?.id}
          okText="تأیید نهایی"
          cancelText="انصراف"
          destroyOnClose
        >
          <Form form={hrForm} layout="vertical" style={{ fontFamily: 'CustomFont' }}>
            <Form.Item name="comment" label="یادداشت">
              <TextArea rows={3} placeholder="در صورت نیاز یادداشت بنویسید" />
            </Form.Item>
          </Form>
        </Modal>

        {/* رد */}
        <Modal
          title={<span style={{ fontFamily: 'CustomFont' }}>رد درخواست مأموریت</span>}
          open={rejectOpen}
          onCancel={() => {
            setRejectOpen(false);
            setRejectTarget(null);
          }}
          onOk={handleReject}
          confirmLoading={actionLoadingId === rejectTarget?.id}
          okText="رد درخواست"
          okButtonProps={{ danger: true }}
          cancelText="انصراف"
          destroyOnClose
        >
          <Form form={rejectForm} layout="vertical" style={{ fontFamily: 'CustomFont' }}>
            <Form.Item
              name="reason"
              label="دلیل رد"
              rules={[
                { required: true, message: 'دلیل رد الزامی است' },
                { min: 3, message: 'دلیل را کامل‌تر بنویسید' },
              ]}
            >
              <TextArea rows={3} placeholder="دلیل رد را وارد کنید" />
            </Form.Item>
          </Form>
        </Modal>

        {/* جزئیات */}
        <Modal
          title={<span style={{ fontFamily: 'CustomFont' }}>جزئیات مأموریت</span>}
          open={detailOpen}
          onCancel={() => {
            setDetailOpen(false);
            setDetailRow(null);
          }}
          footer={
            <Button onClick={() => setDetailOpen(false)} style={{ fontFamily: 'CustomFont' }}>
              بستن
            </Button>
          }
          destroyOnClose
          width={560}
        >
          {detailRow ? (
            <div className="space-y-3 text-sm" style={{ fontFamily: 'CustomFont' }} dir="rtl">
              <div className="flex items-center justify-between gap-2">
                <span className="text-slate-500">وضعیت</span>
                <MissionStatusBadge status={detailRow.status} label={detailRow.status_label} />
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-slate-500">کارمند</span>
                <span>{detailRow.user?.name || '—'}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-slate-500">نوع</span>
                <span>
                  {detailRow.type_label || (detailRow.type === 'hourly' ? 'ساعتی' : 'روزانه')}
                  {' · '}
                  {detailRow.trip_type_label ||
                    (detailRow.trip_type === 'outside_city' ? 'برون‌شهری' : 'درون‌شهری')}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-slate-500">مقصد</span>
                <span className="text-left">{detailRow.destination || '—'}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-slate-500">بازه</span>
                <span className="tabular-nums" dir="ltr">
                  {formatDisplayDate(detailRow, 'start')} → {formatDisplayDate(detailRow, 'end')}
                </span>
              </div>
              {detailRow.type === 'hourly' ? (
                <div className="flex justify-between gap-2">
                  <span className="text-slate-500">ساعت</span>
                  <span className="tabular-nums" dir="ltr">
                    {formatTime(detailRow.start_time)} – {formatTime(detailRow.end_time)}
                  </span>
                </div>
              ) : null}
              <div>
                <div className="text-slate-500 mb-1">هدف</div>
                <div className="bg-slate-50 dark:bg-slate-800 rounded-md p-2">
                  {detailRow.purpose || '—'}
                </div>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-slate-500">هزینه برآوردی</span>
                <span className="tabular-nums">{formatMoney(detailRow.estimated_cost)}</span>
              </div>
              {(detailRow.attachment_path || detailRow.attachment_name) && (
                <div className="flex justify-between items-center gap-2">
                  <span className="text-slate-500 inline-flex items-center gap-1">
                    <PaperClipOutlined /> فایل
                  </span>
                  <Button
                    size="small"
                    type="link"
                    icon={<DownloadOutlined />}
                    style={{ fontFamily: 'CustomFont' }}
                    onClick={() => handleDownload(detailRow)}
                  >
                    {detailRow.attachment_name || 'دانلود'}
                  </Button>
                </div>
              )}
              {detailRow.manager_status ? (
                <div className="border-t pt-2 space-y-1">
                  <div className="font-medium text-slate-700">تأیید مدیر</div>
                  <div className="text-xs text-slate-500">
                    {STATUS_META[detailRow.manager_status]?.label ||
                      (detailRow.manager_status === 'approved'
                        ? 'تأیید شده'
                        : detailRow.manager_status === 'rejected'
                          ? 'رد شده'
                          : detailRow.manager_status === 'pending'
                            ? 'در انتظار'
                            : detailRow.manager_status)}
                    {detailRow.manager_comment ? ` — ${detailRow.manager_comment}` : ''}
                  </div>
                </div>
              ) : null}
              {detailRow.hr_status ? (
                <div className="space-y-1">
                  <div className="font-medium text-slate-700">تأیید منابع انسانی</div>
                  <div className="text-xs text-slate-500">
                    {STATUS_META[detailRow.hr_status]?.label ||
                      (detailRow.hr_status === 'approved'
                        ? 'تأیید شده'
                        : detailRow.hr_status === 'rejected'
                          ? 'رد شده'
                          : detailRow.hr_status === 'pending'
                            ? 'در انتظار'
                            : detailRow.hr_status)}
                    {detailRow.hr_comment ? ` — ${detailRow.hr_comment}` : ''}
                  </div>
                </div>
              ) : null}
              {detailRow.rejection_reason ? (
                <div className="text-rose-600 text-xs">دلیل رد: {detailRow.rejection_reason}</div>
              ) : null}
            </div>
          ) : null}
        </Modal>
      </div>
    </MainLayout>
  );
};

export default MissionRequests;
